<template>
  <v-layout
    row
    justify-center
  >
    <v-dialog
      v-model="isOpen"
      persistent
      max-width="1000px"
    >
      <v-card class="create-bridge-signing">
        <v-form>
          <v-card-title class="toolbar primary white--text">
            <v-icon dark>
              note_add
            </v-icon>
            <h3
              v-if="!document.id"
              class="title ml-2"
            >
              Create Document
            </h3>
            <h3
              v-if="document.id"
              class="title ml-2"
            >
              View Document
            </h3>
            <v-spacer />
            <v-btn
              color="secondary"
              flat
              @click="onToggleOpen"
            >
              X
            </v-btn>
          </v-card-title>
          <v-progress-linear
            v-if="isLoading"
            :indeterminate="true"
            class="ma-0"
          />

          <v-card-text>
            <v-container
              grid-list-md
              class="pa-0"
            >
              <v-layout
                wrap
                class="form-group"
              >
                <v-flex xs12>
                  <h6 class="subheading">
                    Document
                  </h6>
                </v-flex>
                <v-flex
                  xs12
                  md12
                >
                  <v-select
                    v-model="formData.type"
                    :error-messages="getFieldErrors('type')"
                    :items="types()"
                    :disabled="!!document.id"
                    item-key="id"
                    item-text="name"
                    item-value="id"
                    label="Type"
                    @change="e => onSelectChange(e, 'type')"
                  />
                </v-flex>
                <v-flex
                  xs12
                  md6
                >
                  <v-menu
                    v-model="pickerValidityEndOpen"
                    :close-on-content-click="false"
                    full-width
                    max-width="290"
                  >
                    <template v-slot:activator="{ on }">
                      <v-text-field
                        :value="formData.validity_start ? formatDate(formData.validity_start) : ''"
                        :error-messages="getFieldErrors('validity_start')"
                        label="Validity Start"
                        prepend-icon="event"
                        readonly
                        v-on="on"
                      />
                    </template>
                    <v-date-picker
                      :value="formData.validity_start"
                      show-current
                      color="info"
                      @change="e => onDatePickerChange(e, 'validity_start')"
                    />
                  </v-menu>
                </v-flex>
                <v-flex
                  xs12
                  md6
                >
                  <v-menu
                    v-model="pickerValidityStartOpen"
                    :close-on-content-click="false"
                    full-width
                    max-width="290"
                  >
                    <template v-slot:activator="{ on }">
                      <v-text-field
                        :value="formData.validity_end"
                        :error-messages="getFieldErrors('validity_end')"
                        label="Validity End"
                        prepend-icon="event"
                        readonly
                        v-on="on"
                      />
                    </template>
                    <v-date-picker
                      :value="formData.validity_end"
                      show-current
                      color="info"
                      @change="e => onDatePickerChange(e, 'validity_end')"
                    />
                  </v-menu>
                </v-flex>
              </v-layout>
            </v-container>
            <v-alert
              v-for="e in error"
              :key="e.path"
              :value="error"
              type="error"
              color="error"
            >
              {{ e.message }}
            </v-alert>
          </v-card-text>

          <v-card-actions>
            <v-spacer />
            <v-btn
              color="secondary"
              flat
              @click="onToggleOpen"
            >
              Cancel
            </v-btn>
            <v-tooltip
              top
              dark
            >
              <template v-slot:activator="{ on }">
                <v-btn
                  v-if="document.id"
                  :disabled="isLoading || !$v.formData.$anyDirty"
                  :loading="isLoading"
                  color="error"
                  @click="deleteDocument"
                >
                  Delete
                </v-btn>
                <v-btn
                  :disabled="isLoading || !$v.formData.$anyDirty"
                  :loading="isLoading"
                  color="primary"
                  @click="submit"
                  v-on="on"
                >
                  <span v-if="!document.id">Create</span>
                  <span v-if="document.id">Update</span>
                </v-btn>
              </template>
              <span>Validate and submit</span>
            </v-tooltip>
          </v-card-actions>
        </v-form>
      </v-card>
    </v-dialog>
  </v-layout>
</template>

<script>
import { required } from 'vuelidate/lib/validators';
import { formatDate } from '@/utils/dateFormats';

export default {
  name: 'CreateDialog',
  props: {
    document: {
      type: Object,
      required: true,
    },
    taxTraitId: {
      type: String,
      required: true,
    },
    isOpen: {
      type: Boolean,
      required: true,
    },
    toggleOpen: {
      type: Function,
      required: true,
    },
  },
  data: () => ({
    formatDate,
    isOpenProp: this.isOpen,
    isLoading: false,
    pickerValidityStartOpen: false,
    pickerValidityEndOpen: false,
    error: null,
    formData: {
      id: null,
      validity_start: '',
      validity_end: '',
    },
  }),
  validations() {
    return {
      formData: {
        type: { required },
        validity_start: {
          required,
          // eslint-disable-next-line camelcase
          validDateRange(val, { validity_end }) {
            return new Date(val) < new Date(validity_end);
          },
        },
        validity_end: {
          required,
          // eslint-disable-next-line camelcase
          validDateRange(val, { validity_start }) {
            return new Date(val) > new Date(validity_start);
          },
        },
      },
    };
  },
  computed: {
    validFields() {
      return Object.keys(this.$v.formData.$params).filter(
        item => !this.$v.formData[item].$invalid,
      );
    },
    errors() {
      return Object.keys(this.$v.formData.$params).filter(
        item => this.$v.formData[item].$invalid,
      );
    },
  },
  mounted() {
    this.formData = {
      ...this.formData,
      ...(this.serializeDocument(this.document)),
    };

    this.$v.$touch();
  },
  methods: {
    types() {
      const data = this.$store.getters.getPayoutsCreationTypes;
      if (!data || !data.taxTrait || !data.taxTrait.document || !data.taxTrait.document.types) {
        return [];
      }

      return this.transformEnumToSelectArray(data.taxTrait.document.types);
    },
    transformEnumToSelectArray(options) {
      const newOptions = [];
      // eslint-disable-next-line no-restricted-syntax
      for (const [key, value] of Object.entries(options)) {
        newOptions.push({
          id: key,
          name: value,
        });
      }

      return newOptions;
    },
    getFieldErrors(field) {
      const errors = [];
      const formField = this.$v.formData[field];

      if (formField) {
        if (!formField.$dirty || !formField.$invalid) {
          return errors;
        }

        if (!formField.required) {
          errors.push('Required field');
        }

        if (!formField.validDateRange) {
          errors.push('Dates must be in a valid range.');
        }
      }

      return errors;
    },
    onToggleOpen() {
      this.toggleOpen();
      this.error = null;
    },
    deleteDocument() {
      if (!this.document.id) {
        return;
      }
      this.isLoading = true;
      const data = {
        taxTraitId: this.taxTraitId,
        documentId: this.document.id,
      };
      this.$store
        .dispatch('deleteTaxTraitDocument', data)
        .then(() => {
          this.onToggleOpen();
        })
        .catch((err) => {
          this.error = err;
        })
        .finally(() => {
          this.isLoading = false;
        });
    },
    onSelectChange(value, field) {
      this.formData[field] = value;
    },
    onDatePickerChange(date, field) {
      this.formData[field] = date;
      this.closePickers();
    },
    closePickers() {
      this.pickerValidityStartOpen = false;
      this.pickerValidityEndOpen = false;
    },
    submit() {
      this.$v.$touch();
      if (this.$v.$invalid) {
        return;
      }

      this.isLoading = true;
      if (this.document.id) {
        const data = {
          taxTraitId: this.taxTraitId,
          payload: this.serializeFormData(this.formData),
        };
        this.$store
          .dispatch('updateTaxTraitDocument', data)
          .then(() => {
            this.onToggleOpen();
          })
          .catch((err) => {
            this.error = err;
          })
          .finally(() => {
            this.isLoading = false;
          });
      } else {
        const data = {
          taxTraitId: this.taxTraitId,
          payload: this.serializeFormData(this.formData),
        };
        this.$store
          .dispatch('createTaxTraitDocument', data)
          .then(() => {
            this.onToggleOpen();
          })
          .catch((err) => {
            this.error = err;
          })
          .finally(() => {
            this.isLoading = false;
          });
      }
    },
    serializeDocument(document) {
      const { ...serializeDocument } = document;

      return {
        ...serializeDocument,
      };
    },
    serializeFormData(formData) {
      const { ...serializeFormData } = formData;

      return {
        ...serializeFormData,
      };
    },
  },
};
</script>

<style lang="scss">
    .create-bridge-signing {
        .v-card__title {
            padding: 24px;
        }
        .v-card__text {
            max-height: 65vh;
            overflow: auto;
            padding-top: 32px;
        }
        .v-card__actions {
            border-top: 1px solid rgba(0, 0, 0, 0.12);
        }
        .radio-label {
            position: absolute;
            font-size: 16px;
            text-align: left;
            max-width: 133%;
            height: 20px;
            left: 12px;
            line-height: 20px;
            color: rgba(0, 0, 0, 0.54);
            -webkit-transform: translateY(-18px) scale(0.75);
            transform: translateY(0px) translateX(-6px) scale(0.75);
        }
        .subheading {
            padding: 8px;
            background: white;
            color: rgba(0, 0, 0, 0.54);
            top: -20px;
            left: 8px;
            position: absolute;
            display: flex;
            align-items: center;
        }
        .form-group {
            padding: 16px;
            margin: 0 0 24px 0;
            border: 1px solid rgba(0, 0, 0, 0.12);
            border-radius: 5px;
            position: relative;
        }
        .container.grid-list-md .layout:not(:only-child) {
            margin: 0 0 24px 0;
        }
        .v-input--selection-controls.v-input .v-label {
            text-transform: capitalize;
        }
    }
</style>



// WEBPACK FOOTER //
// src/components/payoutsAndTaxes/payout/dialogs/taxTrait/document/CreateDialog.vue